import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../ui/LoadingSpinner';
import { api } from '../../utils/api';

interface MfaSetupModalProps {
    onClose: () => void;
}

const MfaSetupModal: React.FC<MfaSetupModalProps> = ({ onClose }) => {
    const [secret, setSecret] = useState<string | null>(null);
    const [otpAuthUrl, setOtpAuthUrl] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);

    useEffect(() => {
        const fetchMfaSecret = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await api.get<{ otpauth_url: string; base32: string; }>('/api/v1/auth/mfa/generate-secret');
                setSecret(data.base32);
                setOtpAuthUrl(data.otpauth_url);
            } catch (err) {
                if(err instanceof Error) setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchMfaSecret();
    }, []);

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        try {
            await api.post('/api/v1/auth/mfa/verify-setup', { token: verificationCode, secret });
            setIsVerified(true);
        } catch (err) {
            if(err instanceof Error) setError(err.message);
        }
    };
    
    if (isVerified) {
        return (
             <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                <div className="bg-white p-8 rounded-lg shadow-xl text-center max-w-md w-full">
                     <h2 className="text-2xl font-bold text-green-700 mb-4">MFA Setup Complete!</h2>
                     <p className="text-gray-600 mb-6">Your account is now more secure. You will be asked for a verification code on your next login.</p>
                     <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Done</button>
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Secure Your Account</h2>
                <p className="text-gray-600 mb-6">For added security, please set up Multi-Factor Authentication (MFA).</p>
                
                {loading && <LoadingSpinner message="Generating secret..." />}
                {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
                
                {secret && otpAuthUrl && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        <div>
                             <h3 className="text-lg font-semibold mb-2">1. Scan this QR Code</h3>
                             <p className="text-sm text-gray-500 mb-2">Use an authenticator app like Google Authenticator, Authy, or 1Password.</p>
                             <div className="p-4 bg-white border rounded-lg flex justify-center">
                                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpAuthUrl)}`} alt="MFA QR Code" />
                             </div>
                             <p className="text-xs text-gray-500 mt-2">Or manually enter this code:</p>
                             <p className="font-mono bg-gray-100 p-2 rounded text-center break-all">{secret}</p>
                        </div>
                        <form onSubmit={handleVerify}>
                            <h3 className="text-lg font-semibold mb-2">2. Verify Your Device</h3>
                            <p className="text-sm text-gray-500 mb-4">Enter the 6-digit code from your authenticator app.</p>
                            <label htmlFor="mfa-code" className="sr-only">Verification Code</label>
                            <input
                                id="mfa-code"
                                type="text"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-md text-center text-2xl font-mono tracking-widest"
                                placeholder="123456"
                                maxLength={6}
                                required
                            />
                            <button type="submit" className="w-full mt-4 py-3 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700">Verify &amp; Enable</button>
                        </form>
                    </div>
                )}
                 <div className="text-right mt-6">
                    <button onClick={onClose} className="text-gray-500 hover:underline">Skip for now</button>
                </div>
            </div>
        </div>
    );
};

export default MfaSetupModal;